import { useMemo, useState } from 'react';
const generateEmbedScript = (fileName) => {
    return `<script src="path_to_the_file/${fileName}" strategy="afterInteractive"></script>`;
};
const generateScript = (framework, config, themeData, componentName) => {
    const kebabCaseComponentName = componentName.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    const jobPostingScript = `
function render${componentName}() {
  const theme = ${JSON.stringify(themeData, null, 4)};
  const config = ${JSON.stringify(config, (key, value) => {
        if (typeof value === 'function') {
            return `(${value.toString()})`;
        }
        return value;
    }, 4)};
  ${componentName}({elementId:"${kebabCaseComponentName}-widget", config, theme});
}
if (
  document.readyState === "complete" ||
  document.readyState === "interactive"
) {
  render${componentName}();
} else {
  document.addEventListener("DOMContentLoaded", render${componentName});
}
  `;
    switch (framework) {
        case 'React':
            return `
import { useEffect } from 'react';

const useView${componentName} = () => {
  useEffect(() => {

    const script = document.createElement('script');
  
    script.innerHTML = \`${jobPostingScript}\`;
    
    document.body.appendChild(script);
  
    return () => {
      document.body.removeChild(script);
    }
  }, []);
};

export default useView${componentName};
      `;
        case 'Next.js':
            return `
    "use client";
    import Script from 'next/script';

    const ${componentName}Component = () => (
      <>
        <div id="${kebabCaseComponentName}-widget"></div>
        <Script id="job-posting" strategy="lazyOnload">
      {\`${jobPostingScript}\`}
        </Script>
      </>
    );

    export default ${componentName}Component;
      `;
        case 'Angular':
            return `
import { Component, OnInit } from '@angular/core';

@Component({
  selector: 'app-job-posting',
  template: '<div id="${kebabCaseComponentName}-widget"></div>'
})
export class ${componentName}Component implements OnInit {
  ngOnInit() {
    ${jobPostingScript}
  }
}
      `;
        default:
            return `
<div id="${kebabCaseComponentName}-widget"></div>
<script>
  ${jobPostingScript}
</script>
      `;
    }
};
export const useGeneratedScripts = ({ scriptConfig, themeConfig, setIsScriptGenerated, fileInfo, componentName = 'VovSystemWidget', }) => {
    const [generatedScripts, setGeneratedScripts] = useState([]);
    const { fileName, fileUrl } = useMemo(() => {
        if (fileInfo) {
            return fileInfo;
        }
        return {
            fileName: 'vov-system.bundle.js',
            fileUrl: '/vov-system.bundle.js',
        };
    }, [fileInfo]);
    const handleSubmit = (e) => {
        e.preventDefault();
        const formattedComponentName = componentName
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .replace(/\b\w/g, (char) => char.toUpperCase());
        const scripts = [
            {
                title: "Download the scripts and move them to your project's code",
                file: {
                    fileName,
                    fileUrl,
                },
            },
            {
                script: generateEmbedScript(fileName),
                title: `Embed ${formattedComponentName} into the app.`,
                description: 'Copy and paste the following code in your body tag of html code to inject the script.',
                note: 'Replace path_to_the_file with your file path.',
            },
            {
                script: [
                    {
                        framework: 'HTML',
                        script: generateScript('HTML', scriptConfig, themeConfig, componentName),
                    },
                    {
                        framework: 'React',
                        script: generateScript('React', scriptConfig, themeConfig, componentName),
                    },
                    {
                        framework: 'Next.js',
                        script: generateScript('Next.js', scriptConfig, themeConfig, componentName),
                    },
                    {
                        framework: 'Angular',
                        script: generateScript('Angular', scriptConfig, themeConfig, componentName),
                    },
                ],
                title: `Integrate ${formattedComponentName} UI into your framework.`,
                description: `Copy and Paste the following code where you want to launch the ${formattedComponentName} UI`,
                note: 'Make sure main script should available when you execute this code. Update the callback function to set the selected flow ID or perform side effects on flow selection.',
            },
        ];
        setGeneratedScripts(scripts);
        setIsScriptGenerated(true);
    };
    return {
        generatedScripts,
        handleSubmit,
        fileName,
        fileUrl,
    };
};
